import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

// ── Schemas ──────────────────────────────────────────────────────────────────

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const setupSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords don't match",
    path: ['confirm'],
  });

type LoginValues = z.infer<typeof loginSchema>;
type SetupValues = z.infer<typeof setupSchema>;

// ── Helpers ──────────────────────────────────────────────────────────────────

export function PasswordInput({
  placeholder,
  error,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { error?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder ?? '••••••••'}
          autoComplete="current-password"
          {...props}
          className={cn(
            'w-full rounded-lg border bg-background px-3 py-2 pr-10 text-sm shadow-xs outline-none transition-colors',
            'placeholder:text-muted-foreground',
            'focus:border-ring focus:ring-[3px] focus:ring-ring/20',
            error ? 'border-destructive' : 'border-input',
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function TextInput({
  label,
  error,
  id,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }) {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-white/80">{label}</label>
      )}
      <input
        id={id}
        {...props}
        className={cn(
          'w-full rounded-lg border bg-background px-3 py-2 text-sm shadow-xs outline-none transition-colors',
          'placeholder:text-muted-foreground',
          'focus:border-ring focus:ring-[3px] focus:ring-ring/20',
          error ? 'border-destructive' : 'border-input',
        )}
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Login form ────────────────────────────────────────────────────────────────

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  if (pendingEmail) {
    return <SetupForm email={pendingEmail} onSuccess={onSuccess} />;
  }

  return <EmailPasswordForm onNoPassword={(email) => setPendingEmail(email)} onSuccess={onSuccess} />;
}

function EmailPasswordForm({
  onNoPassword,
  onSuccess,
}: {
  onNoPassword: (email: string) => void;
  onSuccess: () => void;
}) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (values: LoginValues) => {
    try {
      await api.post('/api/auth/login', values);
      onSuccess();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { code?: string; error?: string } | undefined;
        if (body?.code === 'no_password') {
          onNoPassword(values.email);
          return;
        }
        setError('root', { message: body?.error ?? e.message });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <TextInput
        id="login-email"
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        error={errors.email?.message}
        {...register('email')}
      />
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="login-password" className="text-sm font-medium text-white/80">Password</label>
        </div>
        <PasswordInput id="login-password" error={errors.password?.message} {...register('password')} />
      </div>

      {errors.root && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive">
          {errors.root.message}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-xs transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
        Sign in
      </button>
    </form>
  );
}

// ── Account activation (first login) ─────────────────────────────────────────

function SetupForm({ email, onSuccess }: { email: string; onSuccess: () => void }) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SetupValues>({
    resolver: zodResolver(setupSchema),
    defaultValues: { email },
  });

  const onSubmit = async (values: SetupValues) => {
    try {
      await api.post('/api/auth/setup-password', { email: values.email, password: values.password });
      onSuccess();
    } catch (e) {
      if (e instanceof ApiError) {
        setError('root', { message: (e.body as { error?: string })?.error ?? e.message });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
        Welcome! This is your first sign-in — please set a password for{' '}
        <strong className="font-semibold">{email}</strong>.
      </div>

      <input type="hidden" {...register('email')} />

      <div>
        <label htmlFor="setup-password" className="mb-1.5 block text-sm font-medium text-white/80">New password</label>
        <PasswordInput
          id="setup-password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          error={errors.password?.message}
          {...register('password')}
        />
      </div>
      <div>
        <label htmlFor="setup-confirm" className="mb-1.5 block text-sm font-medium text-white/80">Confirm password</label>
        <PasswordInput
          id="setup-confirm"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirm?.message}
          {...register('confirm')}
        />
      </div>

      {errors.root && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive">
          {errors.root.message}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-xs transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
        Set password &amp; sign in
      </button>
    </form>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

// Fine polished-stone grain (inline SVG turbulence) — kills gradient banding.
const ONYX_GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/** Decorative emerald orbit line-work anchored off two opposite corners —
 *  concentric hairline rings with a few glowing nodes. Fills the empty flanks
 *  while keeping the centre (the card) clear. Crisp vectors, non-scaling stroke. */
function OnyxVectors() {
  const rings = [72, 124, 182, 246, 318, 398, 486];
  const nodes: [number, number][] = [
    [-118, 96],
    [-58, 214],
    [-300, 60],
  ];
  const Orbit = ({ mirror }: { mirror?: boolean }) => (
    <g transform={mirror ? 'translate(0 862) scale(-1 -1)' : 'translate(1440 64)'}>
      {rings.map((r, i) => (
        <circle
          key={r}
          cx={0}
          cy={0}
          r={r}
          stroke="rgb(39 163 113)"
          strokeOpacity={Math.max(0.04, 0.19 - i * 0.02)}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {nodes.map(([cx, cy]) => (
        <g key={`${cx},${cy}`}>
          <circle cx={cx} cy={cy} r={7} fill="rgb(39 163 113)" fillOpacity={0.14} />
          <circle cx={cx} cy={cy} r={2.5} fill="rgb(52 189 133)" fillOpacity={0.85} />
        </g>
      ))}
    </g>
  );
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden
    >
      <Orbit />
      <Orbit mirror />
    </svg>
  );
}

/** Layered onyx canvas: lit-centre vignette body, diagonal marble veining,
 *  an emerald aura behind the mark, and a fine grain. Pure CSS, no assets. */
function OnyxBackground() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Stone body — near-black with a softly-lit centre */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(125% 90% at 50% 28%, #171c20 0%, #0e1214 40%, #08090b 76%, #050506 100%)',
        }}
      />
      {/* Marble veining — faint light bands sweeping across the stone */}
      <div
        className="absolute inset-0"
        style={{
          mixBlendMode: 'soft-light',
          background: [
            'linear-gradient(118deg, transparent 33%, rgba(214,232,224,0.07) 45%, rgba(214,232,224,0.015) 50%, transparent 61%)',
            'linear-gradient(131deg, transparent 54%, rgba(255,255,255,0.045) 66%, transparent 73%)',
            'linear-gradient(104deg, transparent 12%, rgba(255,255,255,0.03) 20%, transparent 27%)',
          ].join(','),
        }}
      />
      {/* Emerald aura behind the brand mark */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(46% 40% at 50% 20%, rgba(39,163,113,0.18), rgba(39,163,113,0.05) 46%, transparent 72%)',
        }}
      />
      {/* Emerald orbit line-work in the flanks */}
      <OnyxVectors />
      {/* Polished-stone grain */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: ONYX_GRAIN, backgroundSize: '160px 160px' }}
      />
    </div>
  );
}

/** Onyx page shell shared by the local and Clerk auth pages: background,
 *  brand mark, glassy card with title/subtitle, footer hint. */
export function AuthShell({
  title,
  subtitle,
  footer,
  children,
}: {
  title: string;
  subtitle: string;
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      <OnyxBackground />

      <div className="relative w-full max-w-sm">
        {/* Brand mark */}
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <img
            src="/logo.png"
            alt="AxionCRM"
            className="size-9 rounded-[8px] object-cover shadow-lg shadow-black/40 ring-1 ring-white/10"
          />
          <span className="text-xl font-semibold tracking-[-0.01em] text-white">
            Axion<span className="font-normal text-white/45">CRM</span>
          </span>
        </div>

        {/* Card — glassy onyx panel */}
        <div className="rounded-2xl border border-white/10 bg-[#0f1315]/85 shadow-2xl shadow-black/50 ring-1 ring-white/5 backdrop-blur-xl">
          <div className="border-b border-white/10 px-6 py-5 text-center">
            <h1 className="text-base font-semibold text-white">{title}</h1>
            <p className="mt-0.5 text-sm text-white/50">{subtitle}</p>
          </div>
          <div className="px-6 py-5">{children}</div>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">{footer}</p>
      </div>
    </div>
  );
}

export default function LoginPage({ onSuccess }: { onSuccess: () => void }) {
  return (
    <AuthShell
      title="Sign in to your account"
      subtitle="Access is invite-only"
      footer="No account? Access is by invitation — contact your administrator or support to request one."
    >
      <LoginForm onSuccess={onSuccess} />
    </AuthShell>
  );
}
